/*
 * announcement-bar-fade.js — Abstracta Decor
 *
 * Governed by: Motion & Animation System §4 (Linear easing is reserved for
 * continuous loops, announcement-bar rotation named explicitly), §2 (User
 * control) and §15 / WCAG 2.2.2 (Pause, Stop, Hide).
 *
 * The scroll itself is a pure CSS animation — see component-announcement-
 * fade.css. This file adds:
 *
 *   1. Loop duration computed from the track's real rendered width, so the
 *      messages travel at the same perceived speed whether there are two
 *      short announcements or eight long ones.
 *   2. Click to stop / click again to resume (mouse and keyboard).
 *   3. Touch-and-hold to stop on mobile. Hover does not exist on a phone,
 *      so without this the only way to hold a message still is a latching
 *      tap — which then has to be tapped again to undo. Press-and-hold
 *      matches what a visitor instinctively does when they want to read
 *      something that is moving: they put a finger on it. Releasing
 *      resumes. A short tap still latches, so both gestures work.
 *
 * ON prefers-reduced-motion
 * -------------------------
 * This bar deliberately does NOT branch on prefers-reduced-motion — the
 * marquee runs for every visitor. That is a merchant decision, taken so
 * the bar looks identical on every device, and it is recorded here so it
 * is not later mistaken for an oversight and "fixed".
 *
 * The trade-off it accepts: a visitor who set that preference did so for a
 * reason, commonly vestibular sensitivity, where sustained horizontal
 * movement in the corner of the eye can cause real nausea. What keeps this
 * defensible is that stopping the bar stays one gesture away by every
 * input method — hover, click, keyboard focus, press-and-hold — which is
 * what WCAG 2.2.2 actually requires of moving content. Those controls are
 * therefore load-bearing here, not a nicety. Do not remove one without
 * replacing it.
 *
 * The static, non-moving layout below is still reachable: it is what the
 * merchant gets by switching auto-rotation off in the theme editor.
 *
 * With this file absent the marquee still runs on the CSS default duration.
 */

if (!customElements.get('announcement-bar-component')) {
  /* A press longer than this is read as "hold to read", not as a tap. The
     synthetic click that follows such a press is swallowed so the bar does
     not latch into a paused state the visitor never asked for. */
  var HOLD_THRESHOLD_MS = 250;

  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.setDuration = this.setDuration.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('.announcement-bar__marquee-track');
      if (!this.track) return;

      if (this.dataset.autoRotate === 'false') {
        this.renderStatic();
        return;
      }

      // Merchant setting is 3-10, lower = faster. Translated into pixels
      // travelled per second so the value means the same thing regardless
      // of how much text sits in the bar.
      var speed = parseInt(this.dataset.speed, 10);
      this.pixelsPerSecond = 110 - (isNaN(speed) ? 5 : speed) * 6;

      this.setDuration();

      // Pause affordances. These are the accommodation for anyone who
      // cannot comfortably watch the bar move (see header note), so all
      // four input paths are wired, not just the mouse.
      this.addEventListener('click', this.onClick);
      this.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.addEventListener('touchend', this.onTouchEnd, { passive: true });
      this.addEventListener('touchcancel', this.onTouchEnd, { passive: true });

      // Reachable by keyboard, so :focus-within can pause it and the click
      // handler can be triggered with Enter or Space.
      if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
      this.setAttribute('title', this.getAttribute('data-pause-hint') || 'Click to pause');

      // Fonts landing after first paint change the track width, so recompute
      // once they are ready rather than locking in a pre-swap measurement.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this.setDuration);
      }

      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(this.setDuration);
        this.observer.observe(this.track);
      } else {
        window.addEventListener('resize', this.setDuration);
      }
    }

    disconnectedCallback() {
      // Observers and listeners are released with the element (Dev Rules §6).
      if (this.observer) this.observer.disconnect();
      window.removeEventListener('resize', this.setDuration);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('touchstart', this.onTouchStart);
      this.removeEventListener('touchend', this.onTouchEnd);
      this.removeEventListener('touchcancel', this.onTouchEnd);
    }

    /*
     * Removes the aria-hidden duplicate half of the track. The duplicate
     * exists only so the loop can restart seamlessly; with no loop it is
     * pure overflow, and overflow is what hides the last message.
     */
    renderStatic() {
      var items = this.track.children;
      var half = Math.floor(items.length / 2);
      for (var i = items.length - 1; i >= half; i--) {
        this.track.removeChild(items[i]);
      }
      this.classList.add('announcement-bar--static');
    }

    /* Finger down — hold the messages still while it stays down. */
    onTouchStart() {
      this.touchStartedAt = Date.now();
      this.classList.add('is-touch-held');
    }

    /* Finger up — resume, unless the press was long enough to count as a
       deliberate hold, in which case the trailing click is discarded. */
    onTouchEnd() {
      this.classList.remove('is-touch-held');
      this.wasHold = Date.now() - this.touchStartedAt > HOLD_THRESHOLD_MS;
    }

    onClick(event) {
      // A linked announcement should navigate, not toggle playback.
      if (event.target.closest('a')) return;

      // Swallow the click synthesised at the end of a press-and-hold.
      if (this.wasHold) {
        this.wasHold = false;
        return;
      }

      this.paused = !this.paused;
      this.classList.toggle('is-paused', this.paused);

      var strings = window.accessibilityStrings || {};
      this.setAttribute('title', this.paused ? strings.playSlideshow || 'Click to resume' : strings.pauseSlideshow || 'Click to pause');
    }

    setDuration() {
      if (!this.track) return;
      // The track renders the block list twice; one full loop covers half
      // its width, which is the distance the keyframes translate.
      var distance = this.track.scrollWidth / 2;
      if (!distance) return;
      var duration = Math.max(12, distance / this.pixelsPerSecond);
      this.style.setProperty('--announcement-marquee-duration', duration.toFixed(2) + 's');
    }
  }

  customElements.define('announcement-bar-component', AnnouncementBar);
}
